import { useEffect, useState, useRef, useCallback } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { Underline } from '@tiptap/extension-underline'
import { TextAlign } from '@tiptap/extension-text-align'
import { TextStyle, Color, FontFamily, FontSize } from '@tiptap/extension-text-style'
import { Highlight } from '@tiptap/extension-highlight'
import { Link } from '@tiptap/extension-link'
import { Image } from '@tiptap/extension-image'
import { Subscript } from '@tiptap/extension-subscript'
import { Superscript } from '@tiptap/extension-superscript'
import { TaskList } from '@tiptap/extension-task-list'
import { TaskItem } from '@tiptap/extension-task-item'
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table'
import { CharacterCount } from '@tiptap/extension-character-count'
import './RichEditor.css'

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000'

const FONTS = [
  { label: 'Varsayılan', value: '' },
  { label: 'Times New Roman', value: "'Times New Roman', serif" },
  { label: 'Arial', value: 'Arial, sans-serif' },
  { label: 'Calibri', value: 'Calibri, sans-serif' },
  { label: 'Verdana', value: 'Verdana, sans-serif' },
  { label: 'Courier New', value: "'Courier New', monospace" },
  { label: 'Georgia', value: 'Georgia, serif' },
]

const SIZES = ['8pt', '9pt', '10pt', '11pt', '12pt', '14pt', '16pt', '18pt', '24pt', '36pt']

function Sep() { return <span className="re-sep" /> }

function Btn({
  onClick, active = false, danger = false, title, children, disabled = false,
}: {
  onClick: () => void
  active?: boolean
  danger?: boolean
  title?: string
  children: React.ReactNode
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      className={`re-btn${active ? ' re-active' : ''}${danger ? ' re-danger' : ''}`}
      onClick={onClick}
      title={title}
      disabled={disabled}
    >
      {children}
    </button>
  )
}

export interface RichEditorProps {
  value: string
  onChange: (val: string) => void
  minHeight?: number
  placeholder?: string
  disabled?: boolean
}

export default function RichEditor({ value, onChange, minHeight = 200, placeholder, disabled = false }: RichEditorProps) {
  const [htmlMod, setHtmlMod] = useState(false)
  const [htmlSrc, setHtmlSrc] = useState('')
  const [linkDlg, setLinkDlg] = useState(false)
  const [linkUrl, setLinkUrl] = useState('')
  const [resimYukleniyor, setResimYukleniyor] = useState(false)

  const fileRef = useRef<HTMLInputElement>(null)
  const colorRef = useRef<HTMLInputElement>(null)
  const hlRef = useRef<HTMLInputElement>(null)
  const linkInputRef = useRef<HTMLInputElement>(null)

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3, 4] } }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TextStyle,
      Color,
      FontFamily,
      FontSize,
      Highlight.configure({ multicolor: true }),
      Link.configure({ openOnClick: false, HTMLAttributes: { target: '_blank', rel: 'noopener' } }),
      Image.configure({ inline: false, allowBase64: false }),
      Subscript,
      Superscript,
      TaskList,
      TaskItem.configure({ nested: true }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      CharacterCount,
    ],
    content: value || '',
    editable: !disabled,
    onUpdate: ({ editor }) => { if (!disabled) onChange(editor.getHTML()) },
  })

  useEffect(() => {
    if (editor && value !== undefined && editor.getHTML() !== value) {
      editor.commands.setContent(value || '', { emitUpdate: false })
    }
  }, [value])  // eslint-disable-line

  useEffect(() => {
    if (editor) editor.setEditable(!disabled)
  }, [disabled, editor])

  const toggleHtml = () => {
    if (!htmlMod) {
      setHtmlSrc(editor?.getHTML() || '')
      setHtmlMod(true)
    } else {
      editor?.commands.setContent(htmlSrc, { emitUpdate: false })
      onChange(htmlSrc)
      setHtmlMod(false)
    }
  }

  const resimYukle = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setResimYukleniyor(true)
    try {
      const form = new FormData()
      form.append('dosya', file)
      const token = localStorage.getItem('token')
      const res = await fetch(`${API_BASE}/api/bilgi-notu/upload-resim`, {
        method: 'POST',
        body: form,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      if (!res.ok) throw new Error(await res.text())
      const { url } = await res.json()
      editor?.chain().focus().setImage({ src: `${API_BASE}${url}` }).run()
    } catch (err: any) {
      alert('Resim yüklenemedi: ' + (err?.message || err))
    } finally {
      setResimYukleniyor(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const linkAc = () => {
    if (!editor) return
    const mevcut = editor.getAttributes('link').href || ''
    setLinkUrl(mevcut)
    setLinkDlg(true)
    setTimeout(() => linkInputRef.current?.focus(), 50)
  }

  const linkUygula = useCallback(() => {
    if (!editor) return
    if (!linkUrl.trim()) {
      editor.chain().focus().unsetLink().run()
    } else {
      const href = linkUrl.startsWith('http') ? linkUrl : 'https://' + linkUrl
      editor.chain().focus().setLink({ href }).run()
    }
    setLinkDlg(false)
    setLinkUrl('')
  }, [editor, linkUrl])

  const linkKaldir = useCallback(() => {
    editor?.chain().focus().unsetLink().run()
    setLinkDlg(false)
  }, [editor])

  if (!editor) return null

  const inTable = editor.isActive('table')
  const currentColor = editor.getAttributes('textStyle').color || '#000000'
  const currentHL = editor.getAttributes('highlight').color || '#ffff00'
  const currentFont = editor.getAttributes('textStyle').fontFamily || ''
  const currentSize = editor.getAttributes('textStyle').fontSize || ''

  const headingValue = (() => {
    for (let i = 1; i <= 4; i++) {
      if (editor.isActive('heading', { level: i })) return `h${i}`
    }
    return 'p'
  })()

  return (
    <div className={`re-wrap${disabled ? ' re-disabled' : ''}`}>
      {!disabled && (
        <div className="re-toolbar">
          <Btn onClick={() => editor.chain().focus().undo().run()}
            title="Geri Al (Ctrl+Z)" disabled={!editor.can().undo()}>↩</Btn>
          <Btn onClick={() => editor.chain().focus().redo().run()}
            title="İleri Al (Ctrl+Y)" disabled={!editor.can().redo()}>↪</Btn>
          <Sep />

          <select className="re-select" value={headingValue} title="Paragraf stili"
            onChange={e => {
              const v = e.target.value
              if (v === 'p') editor.chain().focus().setParagraph().run()
              else editor.chain().focus().toggleHeading({ level: Number(v.slice(1)) as 1|2|3|4 }).run()
            }}>
            <option value="p">Paragraf</option>
            <option value="h1">Başlık 1</option>
            <option value="h2">Başlık 2</option>
            <option value="h3">Başlık 3</option>
            <option value="h4">Başlık 4</option>
          </select>

          <select className="re-select re-select-font" value={currentFont} title="Yazı tipi"
            onChange={e => {
              const v = e.target.value
              if (!v) editor.chain().focus().unsetFontFamily().run()
              else (editor.chain().focus() as any).setFontFamily(v).run()
            }}>
            {FONTS.map(f => (
              <option key={f.value} value={f.value} style={{ fontFamily: f.value || undefined }}>{f.label}</option>
            ))}
          </select>

          <select className="re-select re-select-size" value={currentSize} title="Yazı boyutu"
            onChange={e => {
              const v = e.target.value
              if (!v) (editor.chain().focus() as any).unsetFontSize().run()
              else (editor.chain().focus() as any).setFontSize(v).run()
            }}>
            <option value="">Boyut</option>
            {SIZES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <Sep />

          <Btn onClick={() => editor.chain().focus().toggleBold().run()}
            active={editor.isActive('bold')} title="Kalın (Ctrl+B)"><b>K</b></Btn>
          <Btn onClick={() => editor.chain().focus().toggleItalic().run()}
            active={editor.isActive('italic')} title="İtalik (Ctrl+I)"><i>İ</i></Btn>
          <Btn onClick={() => editor.chain().focus().toggleUnderline().run()}
            active={editor.isActive('underline')} title="Altı Çizili (Ctrl+U)"><u>A</u></Btn>
          <Btn onClick={() => editor.chain().focus().toggleStrike().run()}
            active={editor.isActive('strike')} title="Üstü Çizili"><s>S</s></Btn>
          <Sep />

          <button type="button" className="re-btn re-color-btn" title="Metin Rengi"
            onClick={() => colorRef.current?.click()}>
            <span className="re-color-label" style={{ borderColor: currentColor }}>A</span>
            <input ref={colorRef} type="color" className="re-color-input" defaultValue="#000000"
              onChange={e => editor.chain().focus().setColor(e.target.value).run()} />
          </button>

          <button type="button" className="re-btn re-color-btn" title="Vurgu Rengi"
            onClick={() => hlRef.current?.click()}>
            <span className="re-hl-label" style={{ backgroundColor: currentHL }}>V</span>
            <input ref={hlRef} type="color" className="re-color-input" defaultValue="#ffff00"
              onChange={e => editor.chain().focus().setHighlight({ color: e.target.value }).run()} />
          </button>
          <Sep />

          <Btn onClick={() => editor.chain().focus().toggleSubscript().run()}
            active={editor.isActive('subscript')} title="Alt Simge">x₂</Btn>
          <Btn onClick={() => editor.chain().focus().toggleSuperscript().run()}
            active={editor.isActive('superscript')} title="Üst Simge">x²</Btn>
          <Sep />

          <Btn onClick={() => editor.chain().focus().setTextAlign('left').run()}
            active={editor.isActive({ textAlign: 'left' })} title="Sola Hizala">◀</Btn>
          <Btn onClick={() => editor.chain().focus().setTextAlign('center').run()}
            active={editor.isActive({ textAlign: 'center' })} title="Ortala">◆</Btn>
          <Btn onClick={() => editor.chain().focus().setTextAlign('right').run()}
            active={editor.isActive({ textAlign: 'right' })} title="Sağa Hizala">▶</Btn>
          <Btn onClick={() => editor.chain().focus().setTextAlign('justify').run()}
            active={editor.isActive({ textAlign: 'justify' })} title="İki Yana Yasla">≡</Btn>
          <Sep />

          <Btn onClick={() => editor.chain().focus().toggleBulletList().run()}
            active={editor.isActive('bulletList')} title="Madde İşaretli Liste">• Liste</Btn>
          <Btn onClick={() => editor.chain().focus().toggleOrderedList().run()}
            active={editor.isActive('orderedList')} title="Numaralı Liste">1. Liste</Btn>
          <Btn onClick={() => editor.chain().focus().toggleTaskList().run()}
            active={editor.isActive('taskList')} title="Görev Listesi">☑ Liste</Btn>
          <Sep />

          <Btn onClick={() => editor.chain().focus().liftListItem('listItem').run()} title="Girintiyi Azalt">⇤</Btn>
          <Btn onClick={() => editor.chain().focus().sinkListItem('listItem').run()} title="Girintiyi Artır">⇥</Btn>
          <Sep />

          <Btn onClick={() => editor.chain().focus().toggleBlockquote().run()}
            active={editor.isActive('blockquote')} title="Alıntı Bloğu">"</Btn>
          <Btn onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Yatay Çizgi">—</Btn>
          <Sep />

          <Btn onClick={linkAc} active={editor.isActive('link')} title="Bağlantı Ekle/Düzenle">🔗</Btn>
          <Sep />

          <Btn onClick={() => fileRef.current?.click()} title="Resim Yükle" disabled={resimYukleniyor}>🖼</Btn>
          <Sep />

          <Btn onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
            title="Tablo Ekle">⊞ Tablo</Btn>
          {inTable && <>
            <Btn onClick={() => editor.chain().focus().addColumnAfter().run()} title="Sütun Ekle">+Stn</Btn>
            <Btn onClick={() => editor.chain().focus().deleteColumn().run()} title="Sütun Sil">-Stn</Btn>
            <Btn onClick={() => editor.chain().focus().addRowAfter().run()} title="Satır Ekle">+Str</Btn>
            <Btn onClick={() => editor.chain().focus().deleteRow().run()} title="Satır Sil">-Str</Btn>
            <Btn onClick={() => editor.chain().focus().mergeOrSplit().run()} title="Hücre Birleştir/Böl">⊟</Btn>
            <Btn onClick={() => editor.chain().focus().deleteTable().run()} danger title="Tabloyu Sil">✕Tbl</Btn>
          </>}
          <Sep />

          <Btn onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()} title="Tüm Biçimi Temizle">✕ Biçim</Btn>

          <button type="button"
            className={`re-btn re-html-btn${htmlMod ? ' re-active' : ''}`}
            onClick={toggleHtml}
            title={htmlMod ? 'Görsel Editöre Geç' : 'HTML Kaynağını Düzenle'}>
            {htmlMod ? '👁 Görsel' : '</> HTML'}
          </button>
        </div>
      )}

      {!disabled && linkDlg && (
        <div className="re-link-dlg">
          <input ref={linkInputRef} className="re-link-input" type="url" placeholder="https://..."
            value={linkUrl} onChange={e => setLinkUrl(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') linkUygula(); if (e.key === 'Escape') setLinkDlg(false) }} />
          <button type="button" className="re-link-ok" onClick={linkUygula}>Tamam</button>
          {editor.isActive('link') && (
            <button type="button" className="re-link-rm" onClick={linkKaldir}>Kaldır</button>
          )}
          <button type="button" className="re-link-cancel" onClick={() => setLinkDlg(false)}>✕</button>
        </div>
      )}

      {resimYukleniyor && <div className="re-upload-bar">Resim yükleniyor...</div>}

      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp"
        style={{ display: 'none' }} onChange={resimYukle} />

      {htmlMod ? (
        <textarea className="re-html-src" style={{ minHeight }}
          value={htmlSrc} onChange={e => setHtmlSrc(e.target.value)} spellCheck={false} />
      ) : (
        <EditorContent editor={editor} className="re-content" style={{ minHeight }} />
      )}

      {!disabled && (
        <div className="re-footer">
          <span>{editor.storage.characterCount?.characters?.() ?? 0} karakter</span>
          <span>{editor.storage.characterCount?.words?.() ?? 0} kelime</span>
        </div>
      )}
    </div>
  )
}
